/*
 * Copyright (c) Dito Technologies LLC
 */

import React, {useState} from "react";
import styles from "ui/feature/piece-play/common/piece-renderer/PieceRendererNoteTooltip.module.scss"
import PieceRendererNoteTooltipRow from "ui/feature/piece-play/common/piece-renderer/PieceRendererNoteTooltipRow";
import PieceRendererViewOriginalImage from "ui/feature/piece-play/common/piece-renderer/PieceRendererViewOriginalImage";
import Body from "ui/kit/typography/Body";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {
    PieceRendererNoteTooltip_note$data,
} from "ui/feature/piece-play/common/__generated__/PieceRendererNoteTooltip_note.graphql";
import NoteOriginalImageExtractor from "mutation/NoteOriginalImageExtractor";
import UpdatePieceFileDeleteNote from "mutation/UpdatePieceFileDeleteNote";
import PieceRendererViewAttributes from "ui/feature/piece-play/common/piece-renderer/PieceRendererViewAttributes";
import UpdatePieceFileChangeMIDINoteAndStart from "mutation/UpdatePieceFileChangeMIDINoteAndStart";
import {CanMakeFlat, CanMakeNatural, CanMakeSharp, IsSharp, MIDITOPitch} from "util/pitch";
import PieceRendererNoteChangeDuration from "ui/feature/piece-play/common/piece-renderer/PieceRendererNoteChangeDuration";

interface Props {
    note: PieceRendererNoteTooltip_note$data;
    pieceId: string;
}

const PieceRendererNoteTooltip: React.FC<Props> = ({note, pieceId}) => {

    const pitch = MIDITOPitch(note.midi_number);

    const [tooltipJobId, setTooltipJobId] = useState<string | null>(null)
    const [mode, setMode] = React.useState<"original_image" | "loading" | "attributes" | "change_duration" | null>(null);

    const handleViewOriginalImage = React.useCallback(async () => {
        if (!tooltipJobId) {
            setMode("loading");
            const jobId = await NoteOriginalImageExtractor(pieceId, note.page_id, note.note_id, note.bulb_id);
            setTooltipJobId(jobId);
        }
        setMode("original_image");
    }, [tooltipJobId, pieceId, note.page_id, note.note_id, note.bulb_id]);

    const handleDeleteNote = React.useCallback(async () => {
        await UpdatePieceFileDeleteNote(pieceId, note.bulb_id);
    }, [note.bulb_id, pieceId]);

    const makeNatural = React.useCallback(async () => {
        let dMidi = IsSharp(pitch) ? -1 : 1;
        await UpdatePieceFileChangeMIDINoteAndStart(pieceId, note.bulb_id, note.midi_number + dMidi, note.note_start)
    }, [note.bulb_id, note.midi_number, note.note_start, pieceId, pitch]);

    const makeSharp = React.useCallback(async () => {
        await UpdatePieceFileChangeMIDINoteAndStart(pieceId, note.bulb_id, note.midi_number + 1, note.note_start)
    }, [pieceId, note.bulb_id, note.midi_number, note.note_start]);

    const makeFlat = React.useCallback(async () => {
        await UpdatePieceFileChangeMIDINoteAndStart(pieceId, note.bulb_id, note.midi_number - 1, note.note_start);
    }, [pieceId, note.bulb_id, note.midi_number, note.note_start]);

    const canMakeSharp = React.useMemo(() => {
        return CanMakeSharp(pitch);
    }, [pitch]);

    const canMakeFlat = React.useMemo(() => {
        return CanMakeFlat(pitch);
    }, [pitch]);

    const canMakeNatural = React.useMemo(() => {
        return CanMakeNatural(pitch);
    }, [pitch]);

    const bodyComponent = React.useMemo(() => {
        if (mode === "original_image") {
            return <PieceRendererViewOriginalImage jobId={tooltipJobId}/>;
        } else if (mode === "loading") {
            return <Body>Loading...</Body>;
        } else if (mode === "attributes") {
            return <PieceRendererViewAttributes note={note}/>
        } else if (mode === "change_duration") {
            return <PieceRendererNoteChangeDuration pieceId={pieceId} note={note}/>;
        } else {
            return (
                <>
                    {canMakeNatural && <PieceRendererNoteTooltipRow title={"Make Natural"} onClick={makeNatural}/>}
                    {canMakeSharp && <PieceRendererNoteTooltipRow title={"Make Sharp"} onClick={makeSharp}/>}
                    {canMakeFlat && <PieceRendererNoteTooltipRow title={"Make Flat"} onClick={makeFlat}/>}
                    <PieceRendererNoteTooltipRow title={"Change Duration"} onClick={() => setMode("change_duration")}/>
                    <PieceRendererNoteTooltipRow onClick={handleViewOriginalImage}
                                                 title={"View Note on Original Image"}/>
                    <PieceRendererNoteTooltipRow title={"View Note Attributes"} onClick={() => setMode("attributes")}/>
                    <PieceRendererNoteTooltipRow title={"Delete Note"} onClick={handleDeleteNote}/>
                </>
            )
        }
    }, [mode, tooltipJobId, note, pieceId, canMakeNatural, makeNatural, canMakeSharp, makeSharp, canMakeFlat, makeFlat, handleViewOriginalImage, handleDeleteNote]);

    return (
        <div className={styles.container}>
            {bodyComponent}
        </div>
    );
}

export default createFragmentContainer(PieceRendererNoteTooltip, {
    note: graphql`
    fragment PieceRendererNoteTooltip_note on MusicPieceFileNote {
        page_id
        note_id
        bulb_id
        midi_number
        note_start
        note_duration
        ...PieceRendererViewAttributes_note
        ...PieceRendererNoteChangeDuration_note
    }
    `
});